import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '../lib/api.js';

// ─── Log waste & shift meals into Tally ────────────────────────────────────
// Unified barista quick-entry (CJ 2026-07-15). A compact tile that opens its
// own modal with a Waste / Shift-meal segment. Posts to Monarch's inventory
// routes, which hold the Tally token and forward to Tally's write hooks. Works
// on personal phones (default apiFn = the SPA api() helper) AND the PIN kiosk
// (pass apiFn wrapping kioskApi). staff is stamped from the signed-in user server-side.

const REASONS = [
  { k: 'expired', label: 'Expired' }, { k: 'spoiled', label: 'Spoiled' },
  { k: 'dropped', label: 'Dropped' }, { k: 'overproduction', label: 'Overprod.' },
  { k: 'mistake', label: 'Mistake' }, { k: 'quality', label: 'Quality' },
  { k: 'other', label: 'Other' },
];

const C = {
  ink: '#212121', sub: '#6b7280', line: '#e6e3dc', bg: '#fdfcf7', card: '#fff',
  act: '#2f6fb0', go: '#226b4a', warn: '#b5561f', warnSoft: '#fbeade',
  chipOn: '#3a3350', chip: '#f2f1ee',
};

// Item picker: one box over Tally's /items search — menu items, ingredients,
// and preps together (tokenized, so "turkey and swiss" finds "Turkey & Swiss").
// A no-recipe match is still loggable; we flag it subtly. The pick carries the
// write-hook ref: square_variation_id for menu, item name otherwise.
function ItemPicker({ call, value, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await call('GET', `/api/inventory/item-search?q=${encodeURIComponent(q.trim())}`);
        setResults((r?.matches || []).map((m) => ({
          key: `${m.square_variation_id || m.item}|${m.kind}`,
          label: m.name,
          hasRecipe: m.has_recipe,
          pick: m.square_variation_id
            ? { square_variation_id: m.square_variation_id, label: m.name }
            : { item: m.item, label: m.name },
        })));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q]); // eslint-disable-line

  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${C.line}`, borderRadius: 10, padding: '9px 11px', background: C.bg }}>
        <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{value.label}</span>
        <button onClick={() => onPick(null)} style={{ background: 'none', border: 0, color: C.sub, fontSize: 13, cursor: 'pointer' }}>Change</button>
      </div>
    );
  }

  return (
    <div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search drinks, food, ingredients…"
        style={{ width: '100%', padding: '11px 12px', fontSize: 15, border: `1px solid ${C.line}`, borderRadius: 10, background: C.bg, boxSizing: 'border-box' }} />
      <div style={{ marginTop: 7, maxHeight: 230, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {loading && <div style={{ fontSize: 13, color: C.sub, padding: '6px 2px' }}>Searching…</div>}
        {!loading && results.map((m) => (
          <button key={m.key} onClick={() => onPick(m.pick)}
            style={{ textAlign: 'left', padding: '10px 11px', border: `1px solid ${C.line}`, borderRadius: 9, background: C.card, fontSize: 14, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span>{m.label}</span>
            {!m.hasRecipe && <span style={{ color: C.sub, fontSize: 10.5, whiteSpace: 'nowrap' }}>logs by name</span>}
          </button>
        ))}
        {!loading && q.trim().length >= 2 && results.length === 0 && (
          <div style={{ fontSize: 13, color: C.sub, padding: '6px 2px' }}>No item matches “{q}”.</div>
        )}
      </div>
    </div>
  );
}

function Stepper({ qty, setQty }) {
  const btn = { padding: '9px 15px', fontWeight: 800, fontSize: 16, background: C.card, border: 0, cursor: 'pointer' };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden' }}>
      <button onClick={() => setQty((n) => Math.max(1, n - 1))} style={btn}>–</button>
      <span style={{ minWidth: 40, textAlign: 'center', fontWeight: 700, background: C.bg, padding: '9px 0', fontVariantNumeric: 'tabular-nums' }}>{qty}</span>
      <button onClick={() => setQty((n) => n + 1)} style={btn}>+</button>
    </div>
  );
}

export function InventoryLogCard({ location, user, apiFn }) {
  // Default to the SPA path (api() with x-user-id); the kiosk passes apiFn=kioskApi.
  const call = useMemo(
    () => apiFn || ((method, path, body) => api(method, path, body, { 'x-user-id': String(user.id) })),
    [apiFn, user.id]);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('waste');
  const [item, setItem] = useState(null);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState('drink');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  function openModal(which) { reset(); setTab(which); setOpen(true); }
  function reset() { setItem(null); setQty(1); setReason(''); setNotes(''); setCategory('drink'); setErr(''); setDone(null); }

  const canSubmit = item && (tab === 'meal' || reason) && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true); setErr('');
    const ref = item.square_variation_id ? { square_variation_id: item.square_variation_id } : { item: item.item };
    try {
      if (tab === 'waste') {
        await call('POST', '/api/inventory/waste', {
          location_id: location.id, ...ref, qty, reason,
          ...(item.unit ? { unit: item.unit } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
      } else {
        await call('POST', '/api/inventory/shift-meal', { location_id: location.id, ...ref, qty, category });
      }
      setDone({ what: item.label });
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  }

  if (!location?.id) return null;
  const primary = { padding: '13px', borderRadius: 10, border: 0, fontWeight: 700, fontSize: 15, color: '#fff', width: '100%', cursor: 'pointer' };
  const openBtn = { padding: '9px 14px', borderRadius: 8, border: `1px solid ${C.line}`, fontWeight: 700, fontSize: 13, background: C.card, cursor: 'pointer' };

  return (
    <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>🗑️ Log waste or shift meal · {location.name}</div>
        <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>Dropped a drink? Had lunch? Log it in a few seconds.</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => openModal('waste')} style={openBtn}>Log waste</button>
        <button onClick={() => openModal('meal')} style={openBtn}>Shift meal</button>
      </div>

      {open && (
        <div onClick={() => !busy && setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: 14, maxWidth: 480, width: '100%', margin: '24px auto', padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{location.name}</div>
              <button onClick={() => !busy && setOpen(false)} aria-label="Close" style={{ background: 'none', border: 0, fontSize: 22, color: '#9e9e9e', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {done ? (
              <div style={{ textAlign: 'center', padding: '10px 4px 4px' }}>
                <div style={{ fontSize: 34 }}>✓</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: C.go, marginTop: 4 }}>{tab === 'waste' ? 'Waste logged' : 'Shift meal logged'}</div>
                <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>{done.what} · counted in Tally. A manager can adjust it there if needed.</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={reset} style={{ ...primary, background: C.act }}>Log another</button>
                  <button onClick={() => setOpen(false)} style={{ ...primary, background: '#eef0f3', color: C.ink }}>Done</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 3, background: C.bg, border: `1px solid ${C.line}`, borderRadius: 10, padding: 3, marginBottom: 13 }}>
                  {['waste', 'meal'].map((t) => (
                    <div key={t} onClick={() => { setTab(t); setErr(''); }}
                      style={{ flex: 1, textAlign: 'center', padding: '7px', fontSize: 13, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
                               background: tab === t ? C.card : 'transparent', color: tab === t ? C.ink : C.sub, boxShadow: tab === t ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>
                      {t === 'waste' ? 'Waste' : 'Shift meal'}
                    </div>
                  ))}
                </div>

                {err && <div style={{ background: C.warnSoft, color: C.warn, padding: 10, borderRadius: 8, fontSize: 12.5, marginBottom: 11 }}>{err}</div>}

                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: C.sub, marginBottom: 6 }}>
                  {tab === 'waste' ? 'What was wasted?' : 'What did you have?'}
                </div>
                <ItemPicker call={call} value={item} onPick={setItem} />

                {item && (
                  <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 13 }}>
                    {tab === 'waste' && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: C.sub, marginBottom: 6 }}>Reason</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {REASONS.map((r) => (
                            <button key={r.k} onClick={() => setReason(r.k)}
                              style={{ padding: '7px 12px', borderRadius: 999, fontSize: 13, fontWeight: reason === r.k ? 700 : 500, cursor: 'pointer',
                                       border: `1px solid ${reason === r.k ? 'transparent' : C.line}`, background: reason === r.k ? C.chipOn : C.chip, color: reason === r.k ? '#fff' : C.ink }}>{r.label}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {tab === 'meal' && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: C.sub, marginBottom: 6 }}>Type</div>
                        <div style={{ display: 'flex', gap: 3, background: C.bg, border: `1px solid ${C.line}`, borderRadius: 10, padding: 3, maxWidth: 220 }}>
                          {['meal', 'drink'].map((c) => (
                            <div key={c} onClick={() => setCategory(c)}
                              style={{ flex: 1, textAlign: 'center', padding: '7px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer', textTransform: 'capitalize',
                                       background: category === c ? C.card : 'transparent', color: category === c ? C.ink : C.sub, boxShadow: category === c ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>{c}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'end', gap: 14 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: C.sub, marginBottom: 6 }}>
                          Qty{item.unit ? ` · ${item.unit}` : ''}
                        </div>
                        <Stepper qty={qty} setQty={setQty} />
                      </div>
                      {tab === 'waste' && (
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: C.sub, marginBottom: 6 }}>Notes (optional)</div>
                          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="knocked off the counter"
                            style={{ width: '100%', padding: '10px 11px', fontSize: 14, border: `1px solid ${C.line}`, borderRadius: 10, background: C.bg, boxSizing: 'border-box' }} />
                        </div>
                      )}
                    </div>

                    <button onClick={submit} disabled={!canSubmit}
                      style={{ ...primary, background: canSubmit ? C.go : '#cfd3d8', cursor: canSubmit ? 'pointer' : 'default', marginTop: 2 }}>
                      {busy ? 'Logging…' : tab === 'waste' ? 'Log waste' : 'Log shift meal'}
                    </button>
                    <div style={{ textAlign: 'center', fontSize: 11, color: C.sub }}>Logging as {user.name} · no cost shown to staff</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
